import React, { useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { PermissionKey, QueryType } from '../../../utils/apollo/graphqlSchema';
import { SecondaryNavLinks } from '../../../components/SecondaryNav';
import { analysesVisitsSelector } from '../../../redux/selectors/analysesVisits';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { resetAnalysesVisits } from '../../../redux/slices/analysesVisits';
import usePermission from '../../../utils/hooks/permissionHooks/usePermission';
import useCohortBuilderIdFromLocation from '../../../utils/routes/useCohortBuilderIdFromLocation';
import ReportHistory from '../../../components/ReportHistory';
import { cohortsNavLinks } from './utils';
import useLoadAnalysesVisits from '../../../utils/analyses/useLoadAnalysesVisits';

const analysesVisitQueryTypes = [QueryType.CohortSample];
const analysesVisitPermissionKeys = [PermissionKey.CohortRead];

const Container = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 16px;
  height: 100%;
  width: 100%;
  padding-top: 12px;
  padding-bottom: 12px;
`;
const FixedSection = styled.div`
  padding-right: 12px;
  padding-left: 12px;
`;

const CohortNav = () => {
  const dispatch = useAppDispatch();
  const builderId = useCohortBuilderIdFromLocation();

  const { checkIsPermitted } = usePermission();
  const isPermittedToReadCohortReport = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.CohortReportRead],
  });

  const analysesVisitsList = useAppSelector(analysesVisitsSelector);

  // Load analyses visits to populate History component
  const visitsLoading = useLoadAnalysesVisits(
    analysesVisitQueryTypes,
    analysesVisitPermissionKeys,
  );

  const visitedCohorts = useMemo(() => {
    if (analysesVisitsList?.length > 0) {
      // Cohort reports only
      return analysesVisitsList.filter(
        (analysisVisit) => analysisVisit.queryType === QueryType.CohortSample,
      );
    }

    return [];
  }, [analysesVisitsList]);

  const links = useMemo(
    () =>
      cohortsNavLinks.map((link) => {
        const isPermitted = link?.permissionKeys
          ? checkIsPermitted({
              requiredPermissionKeys: link.permissionKeys,
            })
          : true;
        return {
          ...link,
          isPermitted,
        };
      }),
    [checkIsPermitted],
  );

  // Perform clean-up on unmount
  useEffect(() => {
    return () => {
      dispatch(resetAnalysesVisits());
    };
  }, [dispatch]);

  return (
    <Container>
      <FixedSection>
        <SecondaryNavLinks links={links} />
      </FixedSection>
      {!visitsLoading &&
        visitedCohorts.length > 0 &&
        isPermittedToReadCohortReport && (
          <ReportHistory
            heading={'History'}
            visits={visitedCohorts}
            selectedAnalysisId={builderId}
          />
        )}
    </Container>
  );
};

export default CohortNav;
